import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CAInfoServer, ClusterAnalysis, detMode, FeatureStats } from "./types";
import { ExtendedDriveData } from "./ErasureSet/ErasureSetLayout.types";
import { processDiskList } from "./ErasureSet/ErasureSet.utils";
import { ApiError } from "api/observeApi";
import { EsetMetrics, MapTab } from "./dataMap.types";
import {
  DriveDetailsData,
  getDriveDetailedMetrics,
  getHealthAsync,
  getLiveHealthAsync,
} from "./dataMapThunks";
import { DateTime } from "luxon";
import { DriveProblem, SetCondition } from "./dataMap.utils";
import { LoadingStatus } from "../../common/types";

export interface ClustersState {
  loadingAnalysis: "neverLoaded" | "idle" | "loading" | "failed";
  loadingLiveAnalysis: "neverLoaded" | "idle" | "loading" | "failed";
  driveFeatStatus: "neverLoaded" | "idle" | "loading" | "failed";
  driveDetsStatus: {
    [driveID: string]: LoadingStatus;
  };
  analysis: ClusterAnalysis | null;
  analysisTimestamp: DateTime | null;
  accountId: number | null;
  siteId: number | null;
  errorState: ApiError | null;
  detailsOpen: boolean;
  detailsMode: detMode;
  selectedPool: number;
  selectedEset: string;
  serverEndpoint: string;
  driveEndpoint: string;
  navigationHistory: SetDetailsMode[];
  zoom: "small" | "medium" | "large";
  sortBy: "sets" | "server" | "capacity" | "status" | "used-space";
  drivesInformation: ExtendedDriveData[];
  driveFeatureStats: { [key: string]: FeatureStats };
  drivesFeaturesInfo: boolean;
  poolServers: { [key: number]: CAInfoServer[] };
  driveDetailMetrics: { [drive: string]: DriveDetailsData | null };
  drivesOffline: ExtendedDriveData[];
  drivesHealing: ExtendedDriveData[];
  totalCapacity: number;
  usedCapacity: number;
  tabs: MapTab[];
  selectedTab: number;
  endpointsCommonParts: string[];
  setDrivesOffline: { [poolId: number]: { [setIndex: number]: number } };
  setDrivesCritical: { [poolId: number]: { [setIndex: number]: number } };
  setDrivesHealing: { [poolId: number]: { [setIndex: number]: number } };
  poolSetSize: { [poolId: number]: number };
  esetMetrics: EsetMetrics;
  // show/hide visual features
  plotUsage: boolean;
  displayDriveFeatures: boolean;
  displayErrorDrives: boolean;
  displayHealing: boolean;
  // filters
  filterOfflineDriveServers: boolean;
  filterHealingDriveServers: boolean;
  filterOutOfSpaceDriveServers: boolean;
  filterOutOfInodesDriveServers: boolean;
  filterErrorDriveServers: boolean;
  filterEsetCondition: SetCondition | null;
  filterDriveProblem: DriveProblem | null;
}

export interface SetDetailsMode {
  mode: detMode;
  pool: number;
  value: string;
  initialDetails?: boolean;
}

const initialState: ClustersState = {
  loadingAnalysis: "neverLoaded",
  loadingLiveAnalysis: "neverLoaded",
  driveFeatStatus: "neverLoaded",
  driveDetsStatus: {},
  analysis: null,
  analysisTimestamp: null,
  accountId: null,
  siteId: null,
  errorState: null,
  detailsOpen: false,
  detailsMode: detMode.null,
  selectedPool: 0,
  selectedEset: "",
  serverEndpoint: "",
  driveEndpoint: "",
  navigationHistory: [],
  zoom: "medium",
  sortBy: "server",
  drivesInformation: [],
  driveFeatureStats: {},
  drivesFeaturesInfo: false,
  poolServers: {},
  driveDetailMetrics: {},
  drivesOffline: [],
  drivesHealing: [],
  totalCapacity: 0,
  usedCapacity: 0,
  tabs: [],
  selectedTab: -1,
  endpointsCommonParts: [],
  setDrivesOffline: {},
  setDrivesCritical: {},
  setDrivesHealing: {},
  poolSetSize: {},
  esetMetrics: {
    normal: 0,
    warning: 0,
    severe: 0,
    critical: 0,
    unusable: 0,
  },
  // show/hide visual features
  plotUsage: true,
  displayDriveFeatures: false,
  displayErrorDrives: true,
  displayHealing: true,
  //filters
  filterOfflineDriveServers: false,
  filterHealingDriveServers: false,
  filterOutOfSpaceDriveServers: false,
  filterOutOfInodesDriveServers: false,
  filterErrorDriveServers: false,
  filterEsetCondition: null,
  filterDriveProblem: null,
};

export const dataMapSlice = createSlice({
  name: "dataMap",
  initialState,
  reducers: {
    resetServerMap: (state: ClustersState) => initialState,
    navigateBack: (state: ClustersState) => {
      if (state.navigationHistory.length > 0) {
        const lastConfig =
          state.navigationHistory[state.navigationHistory.length - 2];
        state.navigationHistory.pop();

        state.detailsMode = lastConfig.mode;
        state.selectedPool = lastConfig.pool;

        switch (lastConfig.mode) {
          case detMode.erasureSet:
            state.selectedEset = lastConfig.value;
            break;
          case detMode.server:
            state.serverEndpoint = lastConfig.value;
            break;
          case detMode.drive:
            state.driveEndpoint = lastConfig.value;
            break;
          default:
        }
      }
    },
    setZoom: (state, action: PayloadAction<"small" | "medium" | "large">) => {
      state.zoom = action.payload;
    },
    setSortBy: (
      state,
      action: PayloadAction<
        "sets" | "server" | "capacity" | "status" | "used-space"
      >,
    ) => {
      state.sortBy = action.payload;
      state.drivesInformation = processDiskList(
        state.analysis,
        state.sortBy,
      ).drives;
    },
    addTab: (state, action: PayloadAction<MapTab>) => {
      // dont add duplicated
      let match: MapTab | null = null;
      let index = -1;
      for (let i = state.tabs.length - 1; i >= 0; i--) {
        const t = state.tabs[i];
        if (
          (t.server &&
            action.payload.server &&
            t.server?.endpoint === action.payload.server?.endpoint) ||
          (t.drive &&
            action.payload.drive &&
            t.drive?.endpoint === action.payload.drive?.endpoint) ||
          (t.eset &&
            action.payload.eset &&
            t.eset?.pool_index === action.payload.eset?.pool_index &&
            t.eset?.eset_index === action.payload.eset?.eset_index)
        ) {
          match = t;
          index = i;
          break;
        }
      }
      if (match === null) {
        state.tabs.push(action.payload);
        if (state.tabs.length > 0) {
          state.selectedTab = state.tabs.length - 1;
        }
      } else {
        state.selectedTab = index;
      }
    },
    selectTab: (state, action: PayloadAction<number>) => {
      state.selectedTab = action.payload;
    },
    removeTab: (state, action: PayloadAction<number>) => {
      state.selectedTab = -1;
      if (state.tabs.length > action.payload) {
        state.tabs.splice(action.payload, 1);
        if (state.selectedTab >= state.tabs.length) {
          state.selectedTab = state.tabs.length - 1;
        }
        if (state.tabs.length === 0) {
          state.selectedTab = -1;
        }
      }
    },
    setPlotUsage: (state, action: PayloadAction<boolean>) => {
      state.plotUsage = action.payload;
    },
    setDisplayDriveFeatures: (state, action: PayloadAction<boolean>) => {
      state.displayDriveFeatures = action.payload;
    },
    setFilterOfflineDriveServers: (state, action: PayloadAction<boolean>) => {
      state.filterOfflineDriveServers = action.payload;
    },
    setFilterHealingDriveServers: (state, action: PayloadAction<boolean>) => {
      state.filterHealingDriveServers = action.payload;
    },
    setFilterOutOfSpaceDriveServers: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.filterOutOfSpaceDriveServers = action.payload;
    },
    setFilterOutOfInodesDriveServers: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.filterOutOfInodesDriveServers = action.payload;
    },
    setFilterErrorDriveServers: (state, action: PayloadAction<boolean>) => {
      state.filterErrorDriveServers = action.payload;
    },
    setDisplayErrorDrives: (state, action: PayloadAction<boolean>) => {
      state.displayErrorDrives = action.payload;
    },
    setDisplayHealing: (state, action: PayloadAction<boolean>) => {
      state.displayHealing = action.payload;
    },
    setFilterEsetCondition: (
      state,
      action: PayloadAction<SetCondition | null>,
    ) => {
      state.filterEsetCondition = action.payload;
    },
    setFilterDriveProblem: (
      state,
      action: PayloadAction<DriveProblem | null>,
    ) => {
      state.filterDriveProblem = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHealthAsync.pending, (state, action) => {
        state.loadingAnalysis = "loading";
        state.errorState = null;
      })
      .addCase(getHealthAsync.rejected, (state, action) => {
        state.loadingAnalysis = "failed";
        if (action.payload) {
          // let payload: FetchError = action.payload
          state.errorState = action.payload as ApiError;
        }
      })
      .addCase(getHealthAsync.fulfilled, (state, action) => {
        state.loadingAnalysis = "idle";
        if (action.payload) {
          state.drivesInformation = action.payload.drivesInformation;
          state.analysisTimestamp = action.payload.analysisTimestamp;
          state.analysis = action.payload.analysis;
          state.poolServers = action.payload.poolServers;
          state.setDrivesOffline = action.payload.setDrivesOffline;
          state.setDrivesCritical = action.payload.setDrivesCritical;
          state.setDrivesHealing = action.payload.setDrivesHealing;
          state.poolSetSize = action.payload.poolSetSize;
          state.endpointsCommonParts = action.payload.endpointsCommonParts;
          state.zoom = action.payload.zoom;
          state.drivesOffline = action.payload.drivesOffline;
          state.drivesHealing = action.payload.drivesHealing;
          state.totalCapacity = action.payload.totalCapacity;
          state.usedCapacity = action.payload.usedCapacity;
          state.esetMetrics = action.payload.esetMetrics;
        }
      })
      .addCase(getLiveHealthAsync.pending, (state, action) => {
        state.loadingLiveAnalysis = "loading";
        state.errorState = null;
      })
      .addCase(getLiveHealthAsync.rejected, (state, action) => {
        state.loadingLiveAnalysis = "failed";
        if (action.payload) {
          // let payload: FetchError = action.payload
          state.errorState = action.payload as ApiError;
        }
      })
      .addCase(getLiveHealthAsync.fulfilled, (state, action) => {
        state.loadingLiveAnalysis = "idle";
        if (action.payload) {
          state.drivesInformation = action.payload.drivesInformation;
          state.analysisTimestamp = action.payload.analysisTimestamp;
          state.analysis = action.payload.analysis;
          state.poolServers = action.payload.poolServers;
          state.setDrivesOffline = action.payload.setDrivesOffline;
          state.setDrivesCritical = action.payload.setDrivesCritical;
          state.setDrivesHealing = action.payload.setDrivesHealing;
          state.poolSetSize = action.payload.poolSetSize;
          state.endpointsCommonParts = action.payload.endpointsCommonParts;
          state.zoom = action.payload.zoom;
          state.drivesOffline = action.payload.drivesOffline;
          state.drivesHealing = action.payload.drivesHealing;
          state.totalCapacity = action.payload.totalCapacity;
          state.usedCapacity = action.payload.usedCapacity;
        }
      })
      .addCase(getDriveDetailedMetrics.pending, (state, action) => {
        state.driveDetsStatus[action.meta.arg] = "loading";
      })
      .addCase(getDriveDetailedMetrics.rejected, (state, action) => {
        state.driveDetsStatus[action.meta.arg] = "failed";
      })
      .addCase(getDriveDetailedMetrics.fulfilled, (state, action) => {
        if (action.payload) {
          state.driveDetailMetrics[action.meta.arg] = action.payload;
        } else {
          state.driveDetailMetrics[action.meta.arg] = null;
        }
        state.driveDetsStatus[action.meta.arg] = "idle";
      });
  },
});

export const {
  resetServerMap,
  navigateBack,
  setZoom,
  setSortBy,
  addTab,
  selectTab,
  removeTab,
  setPlotUsage,
  setDisplayDriveFeatures,
  setDisplayErrorDrives,
  setDisplayHealing,
  setFilterOfflineDriveServers,
  setFilterHealingDriveServers,
  setFilterOutOfSpaceDriveServers,
  setFilterOutOfInodesDriveServers,
  setFilterErrorDriveServers,
  setFilterEsetCondition,
  setFilterDriveProblem,
} = dataMapSlice.actions;
export default dataMapSlice.reducer;
